import { cn } from "@/lib/utils";
import { msToShortDuration } from "@/lib/dateTime";
import type { Severity } from "./types";

/**
 * Severity palette (flat colors, dark-theme first)
 */
export const SEV_STYLE: Record<Severity, { label: string; text: string; bg: string; border: string; bar: string }> = {
    critical: { label: 'Critical', text: 'text-red-500', bg: 'bg-red-500/10', border: 'border-red-500/40', bar: 'bg-red-500' },
    high: { label: 'High', text: 'text-orange-500', bg: 'bg-orange-500/10', border: 'border-orange-500/40', bar: 'bg-orange-500' },
    medium: { label: 'Medium', text: 'text-amber-400', bg: 'bg-amber-400/10', border: 'border-amber-400/40', bar: 'bg-amber-400' },
    low: { label: 'Low', text: 'text-sky-400', bg: 'bg-sky-400/10', border: 'border-sky-400/40', bar: 'bg-sky-400' },
    info: { label: 'Info', text: 'text-zinc-400', bg: 'bg-zinc-400/10', border: 'border-zinc-400/40', bar: 'bg-zinc-400' },
};

export function SeverityTag({ severity, className }: { severity: Severity; className?: string }) {
    const s = SEV_STYLE[severity];
    return (
        <span className={cn(
            'inline-block min-w-[4.5rem] rounded-sm border px-1.5 py-px text-center text-2xs font-semibold uppercase tracking-wider',
            s.text, s.bg, s.border, className,
        )}>
            {s.label}
        </span>
    );
}

export function MonoTag({ children, className, onClick, title }: {
    children: React.ReactNode;
    className?: string;
    onClick?: () => void;
    title?: string;
}) {
    return (
        <span
            title={title}
            onClick={onClick}
            className={cn(
                'inline-block rounded-sm border bg-muted/40 px-1.5 py-px font-mono text-xs',
                onClick && 'cursor-pointer hover:bg-muted',
                className,
            )}
        >
            {children}
        </span>
    );
}

export const timeAgo = (ts: number | null | undefined, now = Date.now()) => {
    if (!ts) return '--';
    const diff = now - ts;
    if (diff < 5_000) return 'just now';
    return msToShortDuration(diff, { largest: 2, units: ['d', 'h', 'm', 's'] }) + ' ago';
};

export const fmtDuration = (ms: number | null | undefined) => {
    if (ms === null || ms === undefined) return '--';
    if (ms < 1000) return `${ms}ms`;
    return msToShortDuration(ms, { largest: 2, units: ['h', 'm', 's'] });
};

export const fmtMiB = (mib: number) => {
    if (mib >= 1024) return `${(mib / 1024).toFixed(2)} GiB`;
    return `${mib.toFixed(1)} MiB`;
};

export const fmtNum = (n: number) => n.toLocaleString('en-US');

export const fmtDateTime = (ts: number) => new Date(ts).toLocaleString(window.txBrowserLocale ?? 'en-US', {
    month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
});

export const STATUS_STYLE: Record<string, string> = {
    booting: 'text-amber-400',
    online: 'text-green-500',
    stopped: 'text-zinc-400',
    crashed: 'text-red-500',
    ended: 'text-zinc-400',
};
